function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 *  A component for creating popover dialogs. Only requires child element.
 *
 * <Popper
 *   options={popperOptions}
 *   className="whatever"
 *   onEntered={this.handleEnter}
 *   onExited={this.handleExit}
 *   closeOnBlur // if you want dialog to self close, on click outside
 *   interactive // if popper contents will be interactable
 *   isShown={variable} // controls if its shown or not,
 *   ref={this.popper} // or via ref and this.popper.show() or this.popper.hide()
 *  >
 *   <ChildContent />
 * </Popper>
 */

import React, { Component } from 'react';
import ReactDOM, { flushSync } from 'react-dom';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import PopperJs from 'popper.js';
import { UNSAFE_PortalProvider } from 'react-aria';
import ThemeExport from "../ThemeExport.js";
import "./Popper.css";
import { SpectrumThemeProvider } from "../theme/SpectrumThemeProvider.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var POPPER_CLASS_NAME = 'popper';
var POPPER_CONTAINER_CLASS_NAME = 'popper-container';
var KEEP_IN_PARENT_OPTIONS = {
  placement: 'bottom-end',
  modifiers: {
    preventOverflow: {
      boundariesElement: 'scrollParent',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fn: (data, options) => {
        var _PopperJs$Defaults$mo, _PopperJs$Defaults$mo2;
        var modified = (_PopperJs$Defaults$mo = PopperJs.Defaults.modifiers) === null || _PopperJs$Defaults$mo === void 0 || (_PopperJs$Defaults$mo = _PopperJs$Defaults$mo.preventOverflow) === null || _PopperJs$Defaults$mo === void 0 || (_PopperJs$Defaults$mo2 = _PopperJs$Defaults$mo.fn) === null || _PopperJs$Defaults$mo2 === void 0 ? void 0 : _PopperJs$Defaults$mo2.call(_PopperJs$Defaults$mo, data, options);
        if (modified == null) {
          return data;
        }
        modified.styles.maxHeight = "".concat(document.documentElement.clientHeight - data.offsets.popper.top - 2 * options.padding // Double padding because there is top and bottom to account for
        , "px");
        return modified !== null && modified !== void 0 ? modified : data;
      }
    },
    flip: {
      enabled: false
    }
  }
};
class Popper extends Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "element", void 0);
    _defineProperty(this, "container", void 0);
    _defineProperty(this, "nodeRef", /*#__PURE__*/React.createRef());
    // This is the request animation frame handle number
    _defineProperty(this, "rAF", void 0);
    this.handleEnter = this.handleEnter.bind(this);
    this.handleExit = this.handleExit.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.element = document.createElement('div');
    this.updateContainerClassName(props.containerClassName, props.isMaximized);
    this.container = /*#__PURE__*/React.createRef();

    // cancelAnimationFrame does nothing if the handle isn't recognized
    // requestAnimationFrame provides a non-zero number, so 0 as a default should be safe
    this.rAF = 0;
    var isShown = this.props.isShown;
    this.state = {
      show: isShown,
      popper: null
    };
  }
  componentDidUpdate(prevProps) {
    var _this$props = this.props,
      isShown = _this$props.isShown,
      containerClassName = _this$props.containerClassName,
      isMaximized = _this$props.isMaximized;
    var popper = this.state.popper;
    if (prevProps.containerClassName !== containerClassName || prevProps.isMaximized !== isMaximized) {
      this.updateContainerClassName(containerClassName, isMaximized);
    }
    if (prevProps.isShown !== isShown) {
      cancelAnimationFrame(this.rAF);
      this.rAF = window.requestAnimationFrame(() => {
        if (isShown) {
          this.show();
        } else {
          this.hide();
        }
      });
    }
    if (popper) {
      popper.scheduleUpdate();
    }
  }
  componentWillUnmount() {
    this.destroyPopper(false);
  }
  updateContainerClassName(containerClassName, isMaximized) {
    this.element.className = classNames(POPPER_CONTAINER_CLASS_NAME, containerClassName, {
      maximized: isMaximized
    });
  }

  /** Goes through an element and it's parents until the first visible element is found */
  getVisibleElement(element) {
    if (element == null || element.clientHeight > 0 || element.clientWidth > 0) {
      return element;
    }
    return this.getVisibleElement(element.parentElement);
  }
  initPopper() {
    var _defaultOptions$modif, _optionsProp$modifier;
    var statePopper = this.state.popper;
    var _this$props2 = this.props,
      closeOnBlur = _this$props2.closeOnBlur,
      referenceObject = _this$props2.referenceObject;
    if (statePopper) {
      return;
    }
    if (this.container.current === null) {
      return;
    }
    var _this$props3 = this.props,
      optionsProp = _this$props3.options,
      keepInParent = _this$props3.keepInParent;
    var defaultOptions = keepInParent ? KEEP_IN_PARENT_OPTIONS : {
      placement: 'auto',
      modifiers: {
        preventOverflow: {
          boundariesElement: 'viewport'
        }
      }
    };
    var options = _objectSpread(_objectSpread(_objectSpread({}, defaultOptions), optionsProp), {}, {
      modifiers: _objectSpread(_objectSpread(_objectSpread({}, defaultOptions.modifiers), optionsProp.modifiers), {}, {
        preventOverflow: _objectSpread(_objectSpread({}, (_defaultOptions$modif = defaultOptions.modifiers) === null || _defaultOptions$modif === void 0 ? void 0 : _defaultOptions$modif.preventOverflow), (_optionsProp$modifier = optionsProp.modifiers) === null || _optionsProp$modifier === void 0 ? void 0 : _optionsProp$modifier.preventOverflow)
      })
    });
    document.body.appendChild(this.element);
    var parent = this.getVisibleElement(this.container.current);
    if (parent == null) {
      parent = this.container.current;
    }
    var popper = new PopperJs(referenceObject || parent, this.element, options);
    popper.scheduleUpdate();

    // delayed due to scheduleUpdate
    cancelAnimationFrame(this.rAF);
    this.rAF = window.requestAnimationFrame(() => {
      // If the current focus is not on the .popper or one of its descendants,
      // set the focus to the .popper element. This is necessary for close on
      // blur to work.
      if (closeOnBlur) {
        var popperEl = this.element.querySelector(".".concat(POPPER_CLASS_NAME));
        if (popperEl instanceof HTMLElement && !popperEl.contains(document.activeElement)) {
          popperEl.focus();
        }
      }
    });

    // Needed to make the animation work
    flushSync(() => {
      this.setState({
        popper
      });
    });
  }
  destroyPopper() {
    var updateState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    cancelAnimationFrame(this.rAF);
    var popper = this.state.popper;
    if (!popper) {
      return;
    }
    popper.destroy();

    // If component is exiting and unmounted in
    // the same frame, destroy can be called twice.
    // Check to make sure removeChild isn't called twice.
    if (document.body.contains(this.element)) {
      document.body.removeChild(this.element);
    }
    if (updateState) {
      this.setState({
        popper: null
      });
    }
  }
  show() {
    this.initPopper();

    // Needed to make the animation work
    flushSync(() => {
      this.setState({
        show: true
      });
    });
  }
  hide() {
    this.setState({
      show: false
    });
  }
  scheduleUpdate() {
    var popper = this.state.popper;
    if (popper) popper.scheduleUpdate();
  }
  handleBlur(e) {
    var _this$props4 = this.props,
      closeOnBlur = _this$props4.closeOnBlur,
      onBlur = _this$props4.onBlur;
    if (!(e.relatedTarget instanceof HTMLElement)) {
      return;
    }
    if (!this.element.contains(e.relatedTarget)) {
      onBlur === null || onBlur === void 0 || onBlur(e);
      if (closeOnBlur) {
        this.hide();
      }
    }
  }
  handleEnter() {
    var onEntered = this.props.onEntered;
    onEntered(); // trigger any parent component waiting for enter handler
  }
  handleExit() {
    var onExited = this.props.onExited;
    var show = this.state.show;
    if (!show) {
      this.destroyPopper();
    }
    onExited(); // trigger any parent component waiting for exited handler
  }
  renderContent() {
    var _this$props5 = this.props,
      className = _this$props5.className,
      children = _this$props5.children,
      timeout = _this$props5.timeout,
      interactive = _this$props5.interactive,
      closeOnBlur = _this$props5.closeOnBlur,
      containPortals = _this$props5.containPortals;
    var show = this.state.show;

    // Redirect nested overlays into this Popper's container.
    var content = containPortals ?
    /*#__PURE__*/
    // eslint-disable-next-line react/jsx-pascal-case
    _jsx(UNSAFE_PortalProvider, {
      getContainer: () => this.element,
      children: children
    }) : children;
    return /*#__PURE__*/_jsx(SpectrumThemeProvider, {
      isPortal: true,
      children: /*#__PURE__*/_jsx(CSSTransition, {
        in: show,
        timeout: timeout,
        classNames: "popper-transition",
        onEntered: this.handleEnter,
        onExited: this.handleExit,
        nodeRef: this.nodeRef,
        children: /*#__PURE__*/_jsx("div", {
          ref: this.nodeRef,
          onClick: e => {
            // stop click events from escaping popper
            e.stopPropagation();
          },
          onKeyDown: e => {
            if (e.key === 'Escape') this.hide();
          },
          className: classNames(POPPER_CLASS_NAME, {
            interactive
          }, className),
          onBlur: this.handleBlur,
          tabIndex: closeOnBlur ? -1 : undefined,
          role: "presentation",
          children: /*#__PURE__*/_jsxs("div", {
            className: classNames('popper-content', {
              // Isolates stray positive z-index (e.g. Bootstrap checkboxes)
              // so it can't paint over the portaled overlay sibling.
              'popper-content--contain-portals': containPortals
            }),
            children: [content, /*#__PURE__*/_jsx("div", {
              className: "popper-arrow",
              "x-arrow": ""
            })]
          })
        })
      })
    });
  }
  render() {
    var popper = this.state.popper;
    var dataTestId = this.props['data-testid'];
    return /*#__PURE__*/_jsx("div", {
      className: "popper-parent-container",
      ref: this.container,
      style: {
        display: 'none'
      },
      "data-testid": dataTestId,
      children: popper && /*#__PURE__*/ReactDOM.createPortal(this.renderContent(), this.element)
    });
  }
}
_defineProperty(Popper, "defaultProps", {
  options: {},
  className: '',
  containerClassName: '',
  isMaximized: false,
  timeout: ThemeExport.transitionMs,
  onEntered() {
    // no-op
  },
  onExited() {
    // no-op
  },
  onBlur() {
    // no-op
  },
  isShown: false,
  interactive: false,
  closeOnBlur: false,
  keepInParent: false,
  containPortals: false,
  referenceObject: null,
  'data-testid': undefined
});
export default Popper;
//# sourceMappingURL=Popper.js.map